import React, { useState, useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { debounce } from "lodash-es"
import {
  getFormDisplayCustomTextOnSuccess,
  getFormCustomSuccessText,
  getFormSlug,
  getFormUsage,
  setFormDisplayCustomTextOnSuccess,
  setFormCustomSuccessText,
} from "reduxSlices/formBuilderSlice"
import * as API from "services/api"
import { USAGES_TO_EXCLUDE_FORM_SHOWING_CUSTOM_TEXT_ON_SUBMISSION } from "utils/formHelpers"
import ContentBar from "components/shared/lists/ContentBar"
import Switch from "components/shared/Switch"
import { MultilineTextInput } from "components/shared/inputs"
import { errorToast } from "components/shared/toast"
import { noHTML, noExcelMetacharacters } from "utils/customFormsyValidations"

const TITLE = "Add Custom Text to Form Submission Success Page"
const DESCRIPTION = "When active, an input box will appear below. Text entered will appear on the \"Success\" page after an individual submits a form."
const DEBOUNCE_TIME = 1000
const DEBOUNCE_OPTIONS = {
  leading: false,
  trailing: true,
}

const FormCustomSuccessTextSetting = () => {
  const dispatch = useDispatch()

  const displayCustomTextOnSuccess = useSelector(getFormDisplayCustomTextOnSuccess)
  const customSuccessText = useSelector(getFormCustomSuccessText)
  const formSlug = useSelector(getFormSlug)
  const formUsage = useSelector(getFormUsage)

  const [localSuccessText, setLocalSuccessText] = useState(customSuccessText || "")

  useEffect(() => {
    setLocalSuccessText(customSuccessText || "")
  }, [customSuccessText])

  const debouncedUpdateSuccessText = useCallback(
    debounce(async (text) => {
      const response = await API.updateFormCustomSuccessText({ formSlug, customSuccessText: text })

      if (response.ok) {
        dispatch(setFormCustomSuccessText(text))
      } else {
        errorToast("Something went wrong. Unable to update custom message.")
        setLocalSuccessText(customSuccessText || "")
      }
    }, DEBOUNCE_TIME, DEBOUNCE_OPTIONS),
    [formSlug, customSuccessText, dispatch],
  )

  if (USAGES_TO_EXCLUDE_FORM_SHOWING_CUSTOM_TEXT_ON_SUBMISSION.includes(formUsage)) return null

  const updateDisplayCustomTextOnSuccess = async (value) => {
    const response = await API.updateFormDisplayCustomTextOnSuccess({ formSlug, displayCustomTextOnSuccess: value })

    if (response.ok) {
      dispatch(setFormDisplayCustomTextOnSuccess(value))
    } else {
      errorToast("Something went wrong. Unable to update custom message display setting.")
    }
  }

  const handleSuccessTextChange = (text) => {
    const validateNoHtml = noHTML(null, text)
    if (validateNoHtml !== true) {
      return errorToast(validateNoHtml)
    }

    const validateNoExcelMetacharacters = noExcelMetacharacters(null, text)
    if (validateNoExcelMetacharacters !== true) {
      return errorToast(validateNoExcelMetacharacters)
    }

    const textLength = text.length
    if (textLength > 500) {
      return errorToast("Custom success text cannot exceed 500 characters.")
    }

    setLocalSuccessText(text)
    debouncedUpdateSuccessText(text)
    return true
  }

  return (
    <>
      <ContentBar
        title={TITLE}
        description={DESCRIPTION}
        footer={
          displayCustomTextOnSuccess && (
            <div className="w-full mb-4">
              <MultilineTextInput
                className="bordered-base-input"
                placeholder="Enter custom success message..."
                value={localSuccessText}
                onChange={handleSuccessTextChange}
              />
            </div>
          )
        }
      >
        <div className="flex flex-col items-end">
          <Switch
            checked={displayCustomTextOnSuccess}
            onChange={updateDisplayCustomTextOnSuccess}
          />
        </div>
      </ContentBar>
    </>
  )
}

export default FormCustomSuccessTextSetting
